import React from 'react'
// @ts-ignore
import { Table as NbUiTable } from '@netbasequid/core'
import { useDisplayTheme } from '../../../hooks/useUiState'

import HeaderRenderer from './HeaderRenderer'
import CellRenderer from './CellRenderer'
import FooterRenderer from './FooterRenderer'
import { ColDefs, TableConfigs } from './types'
import useSort from './sort'
import { Theme } from '../../../features/ui/types'
import { AutomationId } from '../../../utils/automationUtil'

export type TableProps<TRow, TContext, TField extends keyof TRow> = {
  /* displayed data */
  rowData: TRow[]
  /* data shared within the whole table */
  /* will pass an object containing props 'row' and 'theme' by default. */
  context?: TContext
  /* column definitions, define the behavior of each column */
  colDefs: ColDefs<TRow, TContext, TField>
  /* display table foot or not */
  isDisplayFoot: boolean
  /* includes general table configurations */
  configs?: TableConfigs<TRow>
  onRowClick?: (row: TRow) => void
}

export const getCellIndexOffsetByColDefs = <TRow, TContext, TField>(
  colDefs: ColDefs<TRow, TContext, TField>
) => {
  let cellCount = 0
  const result = []
  for (let i = 0; i < colDefs.length; i += 1) {
    result.push(cellCount)
    cellCount += colDefs[i].children?.length || 1
  }
  return result
}

const Table = <TRow, TContext, TField extends keyof TRow>({
  rowData,
  context,
  colDefs,
  isDisplayFoot,
  configs,
  onRowClick
}: TableProps<TRow, TContext, TField>): React.ReactElement => {
  const theme = useDisplayTheme() as Theme
  const contextWithTheme = { ...context, theme }

  const cellIndexOffset = getCellIndexOffsetByColDefs<TRow, TContext, TField>(colDefs)

  // sort
  const [sort, order, orderBy, handleRequestSort] = useSort<TRow>(configs?.colSortingConfigs)
  const displayedData = sort(rowData, colDefs)

  return (
    <NbUiTable mode={theme} order={order} orderBy={orderBy} onRequestSort={handleRequestSort}>
      <NbUiTable.TableHead>
        <NbUiTable.TableRow>
          {colDefs.map((colDef, colIndex) => (
            <HeaderRenderer
              testId={`${AutomationId.table.header}-${colIndex}`}
              key={colDef.field as React.Key}
              colDef={colDef}
              context={contextWithTheme}
            />
          ))}
        </NbUiTable.TableRow>
      </NbUiTable.TableHead>
      <NbUiTable.TableBody>
        {displayedData.map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <NbUiTable.TableRow
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            testId={`${AutomationId.table.row}-${rowIndex}`}
            key={configs?.keyField ? `${row[configs?.keyField]}${rowIndex}` : rowIndex}>
            {colDefs.map((colDef, colIndex) => (
              <CellRenderer
                key={colDef.field as React.Key}
                colDef={colDef}
                context={{ row, ...contextWithTheme }}
                indexOffset={cellIndexOffset[colIndex]}
              />
            ))}
          </NbUiTable.TableRow>
        ))}
      </NbUiTable.TableBody>
      {isDisplayFoot && (
        <NbUiTable.TableFoot>
          <NbUiTable.TableRow>
            {colDefs.map((colDef, colIndex) => (
              <FooterRenderer
                testId={`${AutomationId.table.footer}-${colIndex}`}
                key={colDef.field as React.Key}
                colDef={colDef}
                context={contextWithTheme}
              />
            ))}
          </NbUiTable.TableRow>
        </NbUiTable.TableFoot>
      )}
    </NbUiTable>
  )
}

export default Table
export * from './types/table'
